import { useState, useEffect } from 'react';
import { X, Lightbulb, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Contextual help tooltip that shows once per user session
 * Used to guide first-time users through the wizard
 */
export const OnboardingTooltip = ({ 
  id, 
  title, 
  description, 
  position = 'bottom',
  showOnce = true,
  children 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const storageKey = `onboarding_tooltip_${id}_dismissed`;

  useEffect(() => {
    // Check if user has dismissed this tooltip before
    if (showOnce) {
      const dismissed = localStorage.getItem(storageKey);
      if (!dismissed) {
        // Show with a small delay to avoid overwhelming users
        const timer = setTimeout(() => setIsVisible(true), 1000);
        return () => clearTimeout(timer);
      }
    } else {
      setIsVisible(true);
    }
  }, [storageKey, showOnce]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (showOnce) {
      localStorage.setItem(storageKey, 'true');
    }
  };

  const positionClasses = {
    top: 'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    left: 'right-full mr-2 top-1/2 -translate-y-1/2',
    right: 'left-full ml-2 top-1/2 -translate-y-1/2'
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-brand-500',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-brand-500',
    left: 'left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-brand-500',
    right: 'right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-brand-500'
  };

  return (
    <div className="relative inline-block">
      {children}
      
      {isVisible && (
        <div 
          className={`absolute z-50 ${positionClasses[position]} animate-fade-in`}
          role="tooltip"
        >
          <div className="bg-brand-500 text-white rounded-xl p-4 shadow-lg max-w-xs">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                {title && (
                  <h4 className="font-semibold text-sm mb-1">{title}</h4>
                )}
                <p className="text-sm text-white/90 leading-relaxed">
                  {description}
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="text-white/70 hover:text-white transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Arrow */}
          <div className={`absolute w-0 h-0 border-8 ${arrowClasses[position]}`} />
        </div>
      )}
    </div>
  );
};

/**
 * Inline help text that appears below form fields
 * Less intrusive than tooltips
 */
export const HelpText = ({ 
  text, 
  icon: Icon = HelpCircle,
  className = '' 
}) => {
  return (
    <div className={`flex items-start gap-2 text-sm text-muted-foreground mt-2 ${className}`}>
      <Icon className="w-4 h-4 flex-shrink-0 mt-0.5 text-brand-400" />
      <span>{text}</span>
    </div>
  );
};

/**
 * First-time user banner that appears at the top of steps
 */
export const FirstTimeHint = ({ 
  id,
  message, 
  actionLabel,
  onAction,
  showOnce = true 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const storageKey = `first_time_hint_${id}_dismissed`;

  useEffect(() => {
    if (showOnce) {
      const dismissed = localStorage.getItem(storageKey);
      if (!dismissed) {
        setIsVisible(true);
      }
    } else {
      setIsVisible(true);
    }
  }, [storageKey, showOnce]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (showOnce) {
      localStorage.setItem(storageKey, 'true');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-brand-50 to-violet-50 dark:from-brand-900/20 dark:to-violet-900/20 border border-brand-200 dark:border-brand-800 rounded-xl p-4 mb-6 animate-fade-in">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-800 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-4 h-4 text-brand-600 dark:text-brand-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-foreground leading-relaxed">
            {message}
          </p>
          {actionLabel && onAction && (
            <Button
              variant="link"
              size="sm"
              onClick={onAction}
              className="text-brand-600 dark:text-brand-400 p-0 h-auto mt-1"
            >
              {actionLabel}
            </Button>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Progress indicator showing "Step X of 8"
 */
export const StepProgressIndicator = ({ 
  currentStep, 
  totalSteps = 8,
  stepName 
}) => {
  const progressPercent = (currentStep / totalSteps) * 100;

  return (
    <div className="bg-card border border-border rounded-xl p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">
          Paso {currentStep} de {totalSteps}
        </span>
        <span className="text-sm text-muted-foreground">
          {stepName}
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-brand-500 to-violet-500 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      {/* Percentage */}
      <div className="mt-2 text-right">
        <span className="text-xs text-muted-foreground">
          {Math.round(progressPercent)}% completado
        </span>
      </div>
    </div>
  );
};

export default {
  OnboardingTooltip,
  HelpText,
  FirstTimeHint,
  StepProgressIndicator
};
